import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { IconMoneybag, IconReceipt, IconWallet } from "@tabler/icons-react";

import RequestWithdrawalModal from "@components/_modals/Billing/RequestWithdrawalModal";
import TabsSelector from "@components/TabsSelector";
import { WALLET_PROVIDERS } from "@helpers/wallets";
import { HelpdeskSupport } from "@fragments";

import ConnectedAccount from "./components/ConnectedAccount";
import FundsProjection from "./components/FundsProjection";
import StripePayoutSummary from "./components/StripePayoutSummary";
import Taxes from "./components/Taxes";
import WithdrawalsTable from "./components/WithdrawalsTable";

import "./styles.scss";

// Built from the caller's `t` rather than declared at module level, so every
// label follows the active language. `url` and `key` are routing data and stay
// untranslated.
const getWithdrawalHelpdeskArticles = (t) => [
  {
    title: t("helpdesk.plans.title"),
    subtitle: t("helpdesk.plans.subtitle"),
    url: "https://help.ginastee.com/pt-br/vendas-planos-e-cupons/como-criar-planos-para-vender-sua-comunidade#onde-acessar",
    icon: <IconReceipt />,
  },
  {
    title: t("helpdesk.wallet.title"),
    subtitle: t("helpdesk.wallet.subtitle"),
    url: "https://help.ginastee.com/pt-br/financeiro-wallet-e-saques/como-configurar-sua-wallet-no-ginastee#status-de-analise",
    icon: <IconWallet />,
  },
  {
    title: t("helpdesk.withdrawal.title"),
    subtitle: t("helpdesk.withdrawal.subtitle"),
    url: "https://help.ginastee.com/pt-br/financeiro-wallet-e-saques/como-solicitar-um-saque",
    icon: <IconMoneybag />,
  },
];

const getProviderTabs = (t) => [
  { key: WALLET_PROVIDERS.ASAAS, label: t("withdrawals.tabs.national") },
  { key: WALLET_PROVIDERS.STRIPE, label: t("withdrawals.tabs.international") },
];

export default function WithdrawalSection({
  provider = WALLET_PROVIDERS.ASAAS,
  wallet,
  loadingWallet = false,
  reloadWallets,
  onProviderChange,
}) {
  const { t } = useTranslation("banking");
  const REF_modal = React.useRef();
  const { action } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const amountParam = searchParams.get("amount");

  async function handleLoad() {
    await reloadWallets?.();
  }

  useEffect(() => {
    if (action === "request" && REF_modal.current && wallet && !loadingWallet) {
      const requestedAmount = parseInt(amountParam, 10) || 0;
      navigate(`/banking/withdrawal?provider=${provider}`, { replace: true });
      REF_modal.current.open(
        {
          wallet,
          provider,
          amount:
            requestedAmount > 0
              ? requestedAmount
              : parseInt(wallet?.balance?.available, 10) || 0,
        },
        handleLoad,
      );
    }
  }, [action, wallet, loadingWallet, provider, amountParam]);

  function handleRegisterAccount() {
    if (REF_modal.current) {
      REF_modal.current.open(
        { configureOnly: true, provider, wallet },
        handleLoad,
      );
    }
  }

  // Same configure step, but PATCHing the existing key (behind the
  // sensitive-action verification the interceptor runs on the 428).
  function handleChangeAccount() {
    if (REF_modal.current) {
      REF_modal.current.open(
        { configureOnly: true, change: true, provider, wallet },
        handleLoad,
      );
    }
  }

  const isStripe = provider === WALLET_PROVIDERS.STRIPE;

  // `t` changes identity on a language switch, so the labels re-resolve.
  const providerTabs = useMemo(() => getProviderTabs(t), [t]);
  const helpdeskArticles = useMemo(
    () => getWithdrawalHelpdeskArticles(t),
    [t],
  );

  return (
    <div className="withdrawals-screen">
      <div className="withdrawals-provider-tabs">
        <TabsSelector
          tabs={providerTabs}
          value={provider}
          onChange={onProviderChange}
        />
      </div>

      <div className="wallet-info">
        <ConnectedAccount
          provider={provider}
          wallet={wallet}
          loading={loadingWallet}
          onRegisterAccount={handleRegisterAccount}
          onChangeKey={handleChangeAccount}
        />
        <Taxes provider={provider} wallet={wallet} loading={loadingWallet} />
      </div>

      {isStripe && (
        <StripePayoutSummary wallet={wallet} loading={loadingWallet} />
      )}

      <WithdrawalsTable provider={provider} currency={wallet?.currency} />

      {!isStripe && <FundsProjection />}

      <HelpdeskSupport.CardsGroup cards={helpdeskArticles} />

      <RequestWithdrawalModal modalRef={REF_modal} />
    </div>
  );
}
